// client/src/services/moonbeamService.js
//
// Client API wrapper for moonbeam (eCard) endpoints.
//
// CHANGES IN THIS REVISION (Shareable Links & Reactions build):
//   - Added shareable-link methods (create / list / update).
//   - Added reaction methods (record / summary).
//   - Existing methods unchanged.

import api from './api.js';

export const moonbeamService = {
  // ─── Core CRUD ───────────────────────────────────────────
  getMyMoonbeams: (params = '') => api.get(`/moonbeams?${params}`),
  getMoonbeam: (id) => api.get(`/moonbeams/${id}`),
  create: (data) => api.post('/moonbeams', data),
  update: (id, data) => api.put(`/moonbeams/${id}`, data),
  remove: (id) => api.del(`/moonbeams/${id}`),

  // ─── View Card (public, no auth) ─────────────────────────
  //
  // Either token can be present (mutually exclusive):
  //   recipientToken → ?r=  (per-email-recipient view)
  //   linkToken      → ?l=  (shareable-link view)
  //
  // We always send only one. The server resolves attribution from whichever
  // token is present and ignores the other. Calls without a token still
  // render the card; the view is recorded as anonymous in analytics.
  viewCard: (uuid, { recipientToken, linkToken } = {}) => {
    let query = '';
    if (recipientToken) query = `?r=${recipientToken}`;
    else if (linkToken) query = `?l=${linkToken}`;
    return api.get(`/moonbeams/view/${uuid}${query}`, false);
  },

  // ─── Send & Schedule ─────────────────────────────────────
  send: (id, recipients) => api.post(`/moonbeams/${id}/send`, { recipients }),
  schedule: (id, data) => api.post(`/moonbeams/${id}/schedule`, data),

  // ─── Recipients & Analytics ──────────────────────────────
  getRecipients: (id) => api.get(`/moonbeams/${id}/recipients`),
  getAnalytics: (id) => api.get(`/moonbeams/${id}/analytics`),

  // ─── Reschedule ──────────────────────────────────────────
  rescheduleRecipient: (recipientId, data) =>
    api.patch(`/moonbeams/recipients/${recipientId}/reschedule`, data),

  rescheduleAllPending: (moonbeamId, data) =>
    api.patch(`/moonbeams/${moonbeamId}/reschedule-all`, data),

  // ─── Cancel ──────────────────────────────────────────────
  cancelRecipient: (recipientId) =>
    api.del(`/moonbeams/recipients/${recipientId}`),

  cancelAllPending: (moonbeamId) =>
    api.del(`/moonbeams/${moonbeamId}/cancel-all-pending`),

  // ─── Shareable Links ─────────────────────────────────────
  //
  // Generate a trackable link to share manually (text, WhatsApp,
  // Facebook, etc.). Server returns the link record including a
  // `shareUrl` field — that's the full URL to paste anywhere.
  //
  // label is optional. When blank/empty server defaults it to the
  // template name so analytics has something to display.
  createShareableLink: (moonbeamId, label) =>
    api.post(`/moonbeams/${moonbeamId}/shareable-link`, { label: label || '' }),

  // List all shareable links for a moonbeam (active + deactivated, so
  // the UI can show history alongside live links).
  getShareableLinks: (moonbeamId) =>
    api.get(`/moonbeams/${moonbeamId}/shareable-links`),

  // Update a shareable link.
  //   data: { label?, isactive? }
  //
  // Partial update — pass only the fields you want to change. Use
  // isactive=false to deactivate (existing pasted URLs return 410 Gone),
  // isactive=true to reactivate.
  updateShareableLink: (linkId, data) =>
    api.patch(`/moonbeams/shareable-links/${linkId}`, data),

  // ─── Reactions ───────────────────────────────────────────
  //
  // Record a reaction click on a card. Public (no auth) — anyone with
  // the card URL can react.
  //
  //   moonbeamUuid: the short uuid of the card (NOT the numeric id)
  //   emojiId:      which emoji was clicked (from reactionEmojis array
  //                 returned by viewCard)
  //   viewerToken:  same token that was on the card URL when this user
  //                 viewed (recipient token from ?r= OR link token from
  //                 ?l=). Null/undefined for anonymous reactions.
  //   viewerTokenType: 'recipient' or 'link' — must match how the token
  //                    was issued. Returned by viewCard alongside the
  //                    token so the client just echoes it back.
  //
  // Returns the updated reaction summary so the UI can sync local counts.
  recordReaction: (moonbeamUuid, emojiId, viewerToken, viewerTokenType) =>
    api.post(`/reactions/${moonbeamUuid}`, {
      emojiid: emojiId,
      viewerToken: viewerToken || null,
      viewerTokenType: viewerTokenType || null,
    }, false),

  // Get aggregate reaction counts for a card. Used by the public card
  // view to show running totals so each subsequent viewer sees what's
  // accumulated. Public (no auth).
  getReactionSummary: (moonbeamUuid) =>
    api.get(`/reactions/${moonbeamUuid}/summary`, false),
};

export default moonbeamService;